import React from "react";
import { ContentBlock, StandardContentBlock } from "../../../types";

import ImagesRenderer from "../images-renderer/ImagesRenderer";
import FAQBlock from "../faq-block/FAQBlock";
import FeaturePoints from "../feature-points/FeaturePoints";
import FeatureGrid from "../feature-grid/FeatureGrid";
import ParallaxHero from "../../hero/parallax-hero/ParallaxHero";
import IconTiles from "../../feature-tiles/icon-tiles/IconTiles";

import { BlockHeader, type BlockHeadingTag } from "./blocks/BlockHeader";
import { HeroImage } from "./blocks/HeroImage";
import { RichText } from "./blocks/RichText";
import { Links } from "./blocks/Links";
import { Videos } from "./blocks/Videos";
import { Points } from "./blocks/Points";

type Props = {
    block: ContentBlock;
    paddingY?: string;
};

type BlockWithFeaturePoints = ContentBlock & {
    featurePoints: React.ComponentProps<typeof FeaturePoints>;
};

type BlockWithFeatureGrid = ContentBlock & {
    featureGridBlock: React.ComponentProps<typeof FeatureGrid>;
};

const hasFeaturePoints = (block: ContentBlock): block is BlockWithFeaturePoints => {
    return "featurePoints" in block && !!block.featurePoints;
};

const hasFeatureGrid = (block: ContentBlock): block is BlockWithFeatureGrid => {
    return "featureGridBlock" in block && !!block.featureGridBlock;
};

const isStandardContentBlock = (block: ContentBlock): block is StandardContentBlock => {
    return block.type !== "parallaxHero" && block.type !== "iconTiles";
};

const getHeadingTag = (headingLevel?: 1 | 2 | 3 | 4 | 5 | 6): BlockHeadingTag => {
    return `h${headingLevel ?? 3}` as BlockHeadingTag;
};

const ContentBlockRenderer = ({ block, paddingY }: Props) => {
    const { id, type } = block;

    return (
        <section
            key={id}
            data-content-type={type}
            style={{ padding: `${paddingY} 0` }}
        >
            {block.type === "parallaxHero" && (
                <ParallaxHero {...block.parallaxHero} />
            )}

            {isStandardContentBlock(block) && (
                <>
                    <BlockHeader
                        title={block.title}
                        subTitle={block.subTitle}
                        HeadingTag={getHeadingTag(block.headingLevel)}
                    />

                    <HeroImage heroImage={block.heroImage} />

                    {block.content && <RichText html={block.content} />}

                    {block.images?.length ? (
                        <ImagesRenderer images={block.images} />
                    ) : null}

                    {block.links?.length ? (
                        <Links links={block.links} />
                    ) : null}

                    {block.videos?.length ? (
                        <Videos videos={block.videos} />
                    ) : null}

                    {block.points?.length ? (
                        <Points points={block.points} />
                    ) : null}

                    {block.faqs?.length ? (
                        <FAQBlock items={block.faqs} />
                    ) : null}

                    {hasFeaturePoints(block) && (
                        <FeaturePoints {...block.featurePoints} />
                    )}

                    {hasFeatureGrid(block) && (
                        <FeatureGrid {...block.featureGridBlock} />
                    )}

                </>
            )}

            {block.type === "iconTiles" && (
                <>
                    <BlockHeader
                        title={block.title}
                        subTitle={block.subTitle}
                        HeadingTag={getHeadingTag(block.headingLevel)}
                    />
                    <IconTiles
                        dark={block.iconTiles.dark}
                        content={block.iconTiles.content}
                        titleClasses={block.iconTiles.titleClasses}
                        subtitleClass={block.iconTiles.subtitleClass}
                    />
                </>
            )}
        </section>
    );
};

export default ContentBlockRenderer;
